import React from 'react';
import { Stack, TextField, Typography } from '@mui/material';
import type { ExecuteCheckpointDef } from '@/types/run-stage.types';

interface RangeValue {
  min?: number | null;
  max?: number | null;
}

interface CheckpointRangeProps {
  checkpoint: ExecuteCheckpointDef;
  value: unknown;
  onChange: (value: RangeValue) => void;
  error?: string;
  isReadOnly?: boolean;
}

export const CheckpointRange: React.FC<CheckpointRangeProps> = ({
  checkpoint,
  value,
  onChange,
  error,
  isReadOnly,
}) => {
  const current = (typeof value === 'object' && value !== null ? value : {}) as RangeValue;

  return (
    <Stack spacing={1}>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {checkpoint.checkpointName}
        {checkpoint.required && <Typography component="span" color="error"> *</Typography>}
      </Typography>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <TextField
          size="small"
          type="number"
          label="Min"
          placeholder={`Enter minimum ${checkpoint.checkpointName}`}
          value={current.min != null ? String(current.min) : ''}
          onChange={(e) => onChange({ ...current, min: e.target.value === '' ? null : Number(e.target.value) })}
          disabled={isReadOnly}
          sx={{ flex: 1 }}
        />
        <Typography variant="body2" color="text.secondary">to</Typography>
        <TextField
          size="small"
          type="number"
          label="Max"
          placeholder={`Enter maximum ${checkpoint.checkpointName}`}
          value={current.max != null ? String(current.max) : ''}
          onChange={(e) => onChange({ ...current, max: e.target.value === '' ? null : Number(e.target.value) })}
          disabled={isReadOnly}
          sx={{ flex: 1 }}
        />
      </Stack>
      {checkpoint.rangeMin != null && checkpoint.rangeMax != null && (
        <Typography variant="caption" color="text.secondary">
          Allowed range: {checkpoint.rangeMin} – {checkpoint.rangeMax}
        </Typography>
      )}
      {error && (
        <Typography variant="caption" color="error">{error}</Typography>
      )}
    </Stack>
  );
};
